import React, { useRef, useState, useEffect } from "react";
import { MuiTelInput } from "mui-tel-input";

const PREFERRED_COUNTRIES = ["TR", "DE", "US", "GB", "FR", "NL", "RU", "SA"];

/**
 * Telefon input (atoms) – MuiTelInput ile aynı API; label/error atoms stili.
 * onChange(e): e.target = { name, value } (form handleInputChange uyumlu).
 * Ülke listesi input genişliğini geçmez; preferredCountries ile sık kullanılanlar üstte.
 */
const TelInput = ({
  name,
  label,
  value,
  onChange,
  placeholder,
  error = false,
  errorMessage = "",
  disabled = false,
  defaultCountry = "TR",
  wrapperClassName = "",
  labelClassName = "",
  required = false,
  preferredCountries,
  /** Sadece bu ülkeleri göster (örn. ["TR","US","DE"]); verilmezse tüm ülkeler listelenir. Arama kütüphane tarafından desteklenmediği için listeyi kısaltmak için kullanılabilir. */
  onlyCountries,
}) => {
  const wrapperRef = useRef(null);
  const [menuWidth, setMenuWidth] = useState(undefined);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const updateWidth = () => setMenuWidth(el.offsetWidth);
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className={`space-y-2 ${wrapperClassName}`}>
      {label && (
        <label
          className={`block text-sm font-semibold text-gray-700 ${labelClassName}`}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <MuiTelInput
        name={name}
        value={value ?? ""}
        defaultCountry={defaultCountry}
        preferredCountries={preferredCountries ?? PREFERRED_COUNTRIES}
        onlyCountries={onlyCountries}
        onChange={(phoneValue) => {
          const e = { target: { name, value: phoneValue } };
          onChange(e);
        }}
        disabled={disabled}
        placeholder={placeholder}
        variant="outlined"
        MenuProps={{
          sx: { zIndex: 10001 },
          PaperProps: {
            sx: {
              zIndex: 10001,
              maxHeight: 280,
              marginTop: 1,
              width: menuWidth != null ? menuWidth : undefined,
              minWidth: "min(100%, 200px)",
              maxWidth: menuWidth != null ? menuWidth : "100%",
              boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
              borderRadius: "12px",
            },
          },
          MenuListProps: {
            sx: {
              zIndex: 10001,
              maxHeight: "100%",
              overflow: "auto",
              padding: "4px 0",
            },
          },
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
          transformOrigin: { vertical: "top", horizontal: "left" },
          slotProps: { root: { sx: { zIndex: 10001 } } },
        }}
        sx={{
          width: "100%",
          "& .MuiOutlinedInput-root": {
            backgroundColor: "#fff",
            borderRadius: "16px",
            "& fieldset": {
              borderColor: error ? "#fca5a5" : "#e5e7eb",
              borderWidth: "1px",
            },
            "&:hover fieldset": {
              borderColor: error ? "#f87171" : "#d1d5db",
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? "#ef4444" : "#3b82f6",
              borderWidth: "2px",
            },
          },
          "& .MuiInputLabel-root": {
            color: "#6b7280",
            fontSize: "14px",
          },
          "& .MuiInputLabel-shrink": {
            backgroundColor: "#fff",
          },
          "& .MuiOutlinedInput-input": {
            padding: "12px 14px",
          },
        }}
      />
      {error && errorMessage && (
        <p className="text-red-500 text-xs mt-1">{errorMessage}</p>
      )}
    </div>
  );
};

export default TelInput;
